import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import type { Granularity, TokenSeriesPoint } from '../../api/types';

interface Props {
  points: TokenSeriesPoint[];
  loading?: boolean;
  /** Bucket size — drives x-axis label format. Defaults to 'day'. */
  granularity?: Granularity;
}

function formatBucket(v: string, granularity: Granularity): string {
  if (granularity === 'hour') return `${v.slice(5, 10)} ${v.slice(11, 13)}:00`;
  if (granularity === 'month') return v;
  return v.slice(5);
}

type SeriesKey = 'cacheRead' | 'cacheWrite' | 'output' | 'input';

interface SeriesSpec {
  key: SeriesKey;
  label: string;
  color: string;
  pick: (p: TokenSeriesPoint) => number;
}

// Order matters: ECharts renders the array top-to-bottom in the legend.
// The stack itself stacks in series-declaration order from the bottom up,
// so cache_read (largest) goes first and sits at the bottom of the stack.
const SERIES: SeriesSpec[] = [
  {
    key: 'cacheRead',
    label: 'Cache read',
    color: '#6366f1',
    pick: (p) => p.cacheReadTokens,
  },
  {
    key: 'cacheWrite',
    label: 'Cache write',
    color: '#8b5cf6',
    pick: (p) => p.cacheWriteTokens,
  },
  { key: 'output', label: 'Output', color: '#10b981', pick: (p) => p.outputTokens },
  { key: 'input', label: 'Input', color: '#f59e0b', pick: (p) => p.inputTokens },
];

function formatTokens(n: number): string {
  if (n >= 1e9) return `${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(2)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
  return n.toLocaleString('en-US');
}

function formatUsd(n: number): string {
  if (n >= 1000) return `$${n.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
  if (n >= 1) return `$${n.toFixed(2)}`;
  return `$${n.toFixed(4)}`;
}

export function TokenStackedAreaChart({
  points,
  loading,
  granularity = 'day',
}: Props): JSX.Element {
  const dates = points.map((p) => p.date);
  const byDate = new Map(points.map((p) => [p.date, p]));

  const option: EChartsOption = {
    grid: { left: 64, right: 24, top: 56, bottom: 36 },
    legend: {
      top: 8,
      icon: 'roundRect',
      itemWidth: 12,
      itemHeight: 12,
      textStyle: { fontSize: 12 },
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'line' },
      formatter: (params) => {
        const list = Array.isArray(params) ? params : [params];
        const first = list[0];
        if (!first || typeof first !== 'object' || !('axisValue' in first)) return '';
        const date = String(first.axisValue);
        const point = byDate.get(date);
        if (!point) return '';
        const rows = SERIES.map((s) => {
          const value = s.pick(point);
          const pct = point.totalTokens > 0 ? (value / point.totalTokens) * 100 : 0;
          return `
            <tr>
              <td style="padding:2px 8px 2px 0">
                <span style="display:inline-block;width:10px;height:10px;border-radius:2px;background:${s.color};margin-right:6px;vertical-align:middle"></span>${s.label}
              </td>
              <td style="text-align:right;padding:2px 0">${formatTokens(value)}</td>
              <td style="text-align:right;padding:2px 0 2px 8px;color:#71717a">${pct.toFixed(0)}%</td>
            </tr>`;
        }).join('');
        return `
          <div style="font-weight:600;margin-bottom:4px">${formatBucket(date, granularity)}</div>
          <table style="font-size:12px;border-collapse:collapse">
            ${rows}
            <tr style="border-top:1px solid #e4e4e7">
              <td style="padding:4px 8px 2px 0;font-weight:600">Total</td>
              <td style="text-align:right;padding:4px 0 2px;font-weight:600">${formatTokens(point.totalTokens)}</td>
              <td></td>
            </tr>
            <tr>
              <td style="padding:0 8px 0 0;color:#71717a">Cost (est)</td>
              <td style="text-align:right;color:#71717a">${formatUsd(point.costUsd)}</td>
              <td></td>
            </tr>
          </table>`;
      },
    },
    xAxis: {
      type: 'category',
      data: dates,
      axisLabel: { formatter: (v: string) => formatBucket(v, granularity) },
    },
    yAxis: {
      type: 'value',
      name: 'Tokens',
      nameLocation: 'middle',
      nameGap: 52,
      axisLabel: { formatter: (v: number) => formatTokens(v) },
    },
    series: SERIES.map((s) => ({
      name: s.label,
      type: 'line',
      stack: 'tokens',
      smooth: true,
      symbol: 'none',
      lineStyle: { width: 0 },
      areaStyle: { color: s.color, opacity: 0.85 },
      itemStyle: { color: s.color },
      emphasis: { focus: 'series' },
      data: points.map((p) => s.pick(p)),
    })),
  };

  return (
    <ReactECharts
      option={option}
      style={{ height: 340, width: '100%' }}
      showLoading={loading}
      notMerge
      lazyUpdate
    />
  );
}
